/**
 * Live2D Expression Manager
 * Handles loading and applying expressions to Live2D models
 */

import { createLogger } from '../utils/logger';
import type { Live2DModel } from '../types/live2d';

interface ExpressionParameter {
  Id: string;
  Value: number;
  Blend?: string;
}

interface ExpressionData {
  Parameters?: ExpressionParameter[];
  parameters?: ExpressionParameter[];
}

export class ExpressionManager {
  private logger: ReturnType<typeof createLogger>;
  private expressionCache = new Map<string, ExpressionData>();

  constructor() {
    this.logger = createLogger('ExpressionManager');
  }

  /**
   * Set expression on the model
   */
  async setExpression(
    model: Live2DModel,
    expression: string,
    expressionMappings?: Record<string, string>
  ): Promise<void> {
    if (!model?.internalModel?.coreModel) {
      this.logger.warn('Cannot set expression: model not loaded');
      return;
    }

    try {
      this.logger.info(`Setting expression: ${expression}`);
      this.logger.debug(`Expression mappings:`, expressionMappings);

      // Determine what we're dealing with
      let expressionUrl: string | null = null;
      let useBuiltIn = false;

      // Case 1: Expression is already a full URL - use it directly
      if (this.isFullUrl(expression)) {
        expressionUrl = expression;
        this.logger.debug(`Expression is a full URL: ${expressionUrl}`);
      }
      // Case 2: Expression is a key in the mappings - get the URL
      else if (expressionMappings && expression in expressionMappings) {
        expressionUrl = expressionMappings[expression];
        this.logger.debug(`Found expression mapping: "${expression}" -> "${expressionUrl}"`);
      }
      // Case 3: No mappings exist - try built-in method with just the expression name
      else if (!expressionMappings || Object.keys(expressionMappings).length === 0) {
        useBuiltIn = true;
        this.logger.debug(`No mappings configured, will use built-in method for: ${expression}`);
      }
      // Case 4: Mappings exist but expression not found - this is an error
      else {
        throw new Error(
          `Expression "${expression}" not found in configured mappings. Available: ${Object.keys(expressionMappings).join(', ')}`
        );
      }

      // Now apply the expression
      if (expressionUrl) {
        // We have a URL (either direct or from mapping) - load it
        // IMPORTANT: Do NOT use model.expression() for URLs!
        this.logger.debug(`Loading expression from URL: ${expressionUrl}`);
        await this.loadAndApplyExpression(model, expressionUrl);
        this.logger.info(`Expression "${expression}" loaded from URL successfully`);
      } else if (useBuiltIn) {
        // No URL, no mappings - try the built-in method with just the name
        // This will only work if the model has built-in expressions
        this.logger.debug(`Trying built-in expression: ${expression}`);
        await this.tryBuiltInExpression(model, expression);
      }
    } catch (error) {
      this.logger.error(`Failed to set expression "${expression}":`, error);
      throw error;
    }
  }

  /**
   * Load expression data from file and apply to model
   */
  private async loadAndApplyExpression(model: Live2DModel, expressionPath: string): Promise<void> {
    try {
      // Check cache first
      if (this.expressionCache.has(expressionPath)) {
        const cachedData = this.expressionCache.get(expressionPath)!;
        this.applyExpressionData(model, cachedData);
        return;
      }

      this.logger.debug(`Loading expression from: ${expressionPath}`);

      const response = await fetch(expressionPath);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const expressionData: ExpressionData = await response.json();
      this.logger.debug('Received expression data:', expressionData);

      // Cache the expression data
      this.expressionCache.set(expressionPath, expressionData);

      // Apply the expression
      this.applyExpressionData(model, expressionData);
    } catch (error) {
      this.logger.warn(`Failed to load expression from ${expressionPath}:`, error);
      throw error;
    }
  }

  /**
   * Apply expression data directly to model parameters
   */
  private applyExpressionData(model: Live2DModel, expressionData: ExpressionData): void {
    if (!model?.internalModel?.coreModel) return;

    // Handle different parameter field names
    const parameters = expressionData.Parameters || expressionData.parameters;

    if (!parameters || !Array.isArray(parameters)) {
      throw new Error('Expression data does not contain valid parameters array');
    }

    this.logger.debug(`Applying ${parameters.length} expression parameters`);

    try {
      parameters.forEach(param => {
        const paramId = param.Id;
        const paramValue = param.Value;
        const paramBlend = param.Blend || 'Add';

        const paramIndex = model.internalModel.coreModel.getParameterIndex(paramId);
        if (paramIndex > -1) {
          model.internalModel.coreModel.setParameterValueByIndex(paramIndex, paramValue);
          this.logger.debug(`Applied parameter ${paramId} = ${paramValue} (${paramBlend})`);
        } else {
          this.logger.warn(`Parameter not found in model: ${paramId}`);
        }
      });
    } catch (error) {
      this.logger.error('Error applying expression parameters:', error);
      throw error;
    }
  }

  /**
   * Try using the model's built-in expression method as fallback
   */
  private async tryBuiltInExpression(model: Live2DModel, expression: string): Promise<void> {
    // SAFETY CHECK: Never use this method with URLs!
    if (this.isFullUrl(expression)) {
      throw new Error(`tryBuiltInExpression called with URL: ${expression}. This is a bug!`);
    }

    if (typeof model.expression === 'function') {
      try {
        // Built-in expressions expect simple names like "happy" or "happy.exp3.json"
        // NOT full paths or URLs
        const expressionName = expression.endsWith('.exp3.json')
          ? expression
          : `${expression}.exp3.json`;

        this.logger.debug(`Trying built-in expression method with simple name: ${expressionName}`);

        // This calls the Live2D library's built-in expression loader
        // which will look for the expression relative to the model's location
        await model.expression(expressionName);
        this.logger.info(`Applied built-in expression: ${expressionName}`);
      } catch (error) {
        this.logger.error(`Built-in expression method failed:`, error);
        this.logger.error(`Attempted expression name: ${expression}`);
        // Don't throw - built-in expressions are optional
      }
    } else {
      this.logger.warn(`Model does not support built-in expressions for "${expression}"`);
    }
  }

  /**
   * Preload expressions for better performance
   */
  async preloadExpressions(expressionMappings: Record<string, string>): Promise<void> {
    this.logger.info(`Preloading ${Object.keys(expressionMappings).length} expressions`);

    const loadPromises = Object.entries(expressionMappings).map(async ([name, path]) => {
      try {
        const response = await fetch(path);
        if (response.ok) {
          const data = await response.json();
          this.expressionCache.set(path, data);
          this.logger.debug(`Preloaded expression: ${name}`);
        }
      } catch (error) {
        this.logger.warn(`Failed to preload expression ${name}:`, error);
      }
    });

    await Promise.allSettled(loadPromises);
    this.logger.info('Expression preloading completed');
  }

  /**
   * Get list of available expressions from mappings
   */
  getAvailableExpressions(expressionMappings?: Record<string, string>): string[] {
    if (expressionMappings) {
      return Object.keys(expressionMappings);
    }

    // Return common expression names if no mappings provided
    return ['default', 'happy', 'sad', 'surprised', 'angry', 'thinking', 'excited', 'bored', 'shy'];
  }

  /**
   * Clear expression cache
   */
  clearCache(): void {
    this.expressionCache.clear();
    this.logger.debug('Expression cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.expressionCache.size,
      expressions: Array.from(this.expressionCache.keys()),
    };
  }

  /**
   * Check if a path is a full URL
   */
  private isFullUrl(path: string): boolean {
    return path.startsWith('http://') || path.startsWith('https://');
  }
}
